import React, { useState } from 'react'
import useGetTeams from '@/services/api/team/useGetTeams'
import useSetDefaultTeam from '@/services/api/team/useSetDefaultTeam'
import useCreateTeam from '@/services/api/team/useCreateTeam.js'
import SpaceSelectComponent from './SpaceSelectComponent'
import SpaceSettingsForm from './SpaceSettingsForm'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { Separator } from '@/components/ui/separator'
import { Badge } from '@/components/ui/Badge'
import { TEAM_TYPES } from '@/data/constants/constants'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/AlertDialog'
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Star, Building, LogOut, AlertTriangle, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import useGetTeamDetails from '@/services/api/team/useGetTeamDetails'
import useLeaveTeam from '@/services/api/team/useLeaveTeam'
import useDeleteTeam from '@/services/api/team/useDeleteTeam'
import useAuthStore from '@/store/useAuthStore'
import { Label } from '@/components/ui/Label'
import ResetSpaceDataSection from './ResetSpaceDataSection'

export default function SpaceSettingsSection() {
  const currentTeamId = useAuthStore((state) => state.currentTeamId)

  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(
    currentTeamId,
  )
  const [deletePassword, setDeletePassword] = useState('')

  const { data: teams, refetch: refetchTeams } = useGetTeams()

  // Space type no longer gates access — a personal space can be created freely.
  const existingPersonalSpace = teams?.find((t) => t.team_type === 'Personal') ?? null

  const { createTeam, createTeamStatus } = useCreateTeam({
    setError: () => {},
    clientServerFieldMapping: { teamType: 'team_type' },
  })

  const handleCreatePersonalSpace = async () => {
    await createTeam({ teamType: TEAM_TYPES.PERSONAL })
    refetchTeams()
  }

  const {
    action: setDefaultTeam,
    status: { isPending: setDefaultLoading },
  } = useSetDefaultTeam()
  const {
    action: leaveTeam,
    status: { isPending: leaveLoading },
  } = useLeaveTeam()
  const {
    action: deleteTeam,
    status: { isPending: deleteLoading },
  } = useDeleteTeam()

  const { data: selectedTeam, status: teamDetailsStatus } = useGetTeamDetails({
    teamId: selectedTeamId,
  })

  const isTheSelectedTeamDefault = teams?.find(
    (team) => team.id === selectedTeamId,
  )?.is_default

  const handleSetDefault = async () => {
    if (!selectedTeamId) return

    await setDefaultTeam({ teamId: selectedTeamId })
  }

  const handleLeaveSpace = async () => {
    if (!selectedTeamId) return
    await leaveTeam({ teamId: selectedTeamId })
    setSelectedTeamId(currentTeamId)
  }

  const handleDeleteSpace = async () => {
    if (!selectedTeamId || !deletePassword) return
    await deleteTeam({ teamId: selectedTeamId, password: deletePassword })
    setSelectedTeamId(currentTeamId)
    setDeletePassword('')
  }

  return (
    <div className="space-y-6">
      <div>
        <h2>Space Settings</h2>
        <p className="text-muted-foreground">
          Manage space configuration and your role
        </p>
      </div>

      <Separator />

      <div className="space-y-4">
        {/* Personal Space card — only rendered once teams data is available */}
        {teams !== undefined && (
          <>
            {existingPersonalSpace ? (
              <Card className="w-full p-6">
                <h3>Personal Space</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  You already have a personal space.
                </p>
                <Badge className="mt-2">{existingPersonalSpace.name}</Badge>
              </Card>
            ) : (
              <Card className="w-full p-6">
                <h3>Personal Space</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Create a personal space to manage your personal finances
                  alongside your business.
                </p>
                <Button
                  className="mt-4"
                  onClick={handleCreatePersonalSpace}
                  disabled={createTeamStatus.isPending}
                  loading={createTeamStatus.isPending}
                >
                  Create Personal Space
                </Button>
              </Card>
            )}
            <Separator />
          </>
        )}

        <SpaceSelectComponent
          selectedTeamId={selectedTeamId}
          onValueChange={(val) => {
            setSelectedTeamId(val)
          }}
        />

        {teamDetailsStatus === 'pending' && (
          <div className="flex items-center justify-center">Loading...</div>
        )}

        {selectedTeam && (
          <div className="space-y-6">
            {/* Role and Default Status */}
            <Card className="w-full p-6">
              <div className="mb-4 flex items-center justify-between">
                <h3>Space Information</h3>
                <div className="flex items-center space-x-2">
                  <Building className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    {selectedTeam?.space?.name}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <p className="mb-1 text-sm text-muted-foreground">
                    Your Role
                  </p>
                  {selectedTeam.roles.map((role) => (
                    <Badge key={role}>{role}</Badge>
                  ))}
                </div>

                <div>
                  <p className="mb-1 text-sm text-muted-foreground">
                    Default Space
                  </p>
                  <div className="flex items-center space-x-2">
                    {isTheSelectedTeamDefault ? (
                      <Badge className="border-yellow-200 bg-yellow-100 text-yellow-800">
                        <Star className="mr-1 h-3 w-3" />
                        Default
                      </Badge>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleSetDefault}
                        disabled={setDefaultLoading}
                      >
                        {setDefaultLoading ? (
                          <>
                            <div className="mr-1 h-3 w-3 animate-spin rounded-full border border-current border-t-transparent"></div>
                            Setting...
                          </>
                        ) : (
                          <>
                            <Star className="mr-1 h-3 w-3" />
                            Set as Default
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </Card>

            {/* Space Settings Form */}
            <SpaceSettingsForm
              key={selectedTeamId}
              teamId={selectedTeamId}
              onUpdate={refetchTeams}
            />

            {/* Leave Space Section */}
            <Card className="w-full border-red-200 p-6">
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-red-900">Leave Space</h4>
                  <p className="mt-1 text-sm text-red-700">
                    Remove yourself from this space. You will lose access to all
                    data and cannot undo this action.
                  </p>
                </div>

                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="destructive"
                      size="sm"
                      disabled={leaveLoading || selectedTeam.is_owner}
                    >
                      {leaveLoading ? (
                        <>
                          <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                          Leaving...
                        </>
                      ) : (
                        <>
                          <LogOut className="mr-2 h-4 w-4" />
                          Leave Space
                        </>
                      )}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Leave Space</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to leave "
                        {selectedTeam?.space.name}"? This action cannot be
                        undone and you will lose access to all data in this
                        space.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleLeaveSpace}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        Leave Space
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>

                {selectedTeam.is_owner && (
                  <p className="text-xs text-red-600">
                    {/* IF you are the owner of the space you cannot leave */}
                    You cannot leave the space because you are the owner.
                  </p>
                )}
              </div>
            </Card>

            {/* Delete Space Section - Only for owners */}
            {selectedTeam.is_owner && (
              <Card
                key={selectedTeam.id}
                className="w-full border-red-300 bg-red-50 p-6"
              >
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-red-900">Delete Space</h4>
                    <p className="mt-1 text-sm text-red-700">
                      Permanently delete this space and all its data. This
                      action is irreversible and will remove all users, data,
                      and settings.
                    </p>
                  </div>

                  <Dialog>
                    <DialogTrigger asChild>
                      <Button
                        variant="destructive"
                        size="sm"
                        disabled={deleteLoading}
                      >
                        {deleteLoading ? (
                          <>
                            <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                            Deleting...
                          </>
                        ) : (
                          <>
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete Space
                          </>
                        )}
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader className="flex-col">
                        <DialogTitle className="flex items-center gap-2">
                          Delete Space
                        </DialogTitle>
                        <DialogDescription>
                          Are you absolutely sure you want to delete "
                          {selectedTeam?.space.name}"? This will permanently
                          remove all data, users, and settings associated with
                          this space. This action cannot be undone.
                        </DialogDescription>
                      </DialogHeader>
                      <DialogBody>
                        <div className="">
                          <Label className="mb-2 block text-sm font-medium text-gray-700">
                            Enter your password to confirm:
                          </Label>
                          <Input
                            type="password"
                            value={deletePassword}
                            onChange={(e) => setDeletePassword(e.target.value)}
                            placeholder="Your password"
                            className="w-full"
                          />
                        </div>
                        <DialogFooter>
                          <DialogClose>
                            <Button
                              variant="outline"
                              onClick={() => setDeletePassword('')}
                            >
                              Cancel
                            </Button>
                          </DialogClose>
                          <Button
                            onClick={handleDeleteSpace}
                            disabled={!deletePassword}
                            variant="destructive"
                            className="disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            Delete Space
                          </Button>
                        </DialogFooter>
                      </DialogBody>
                    </DialogContent>
                  </Dialog>
                </div>
              </Card>
            )}

            {/* Danger zone — reset space data, owner only (14zfmu11ywd).
                Resets the space you're in, so other spaces just point there. */}
            {selectedTeam.is_owner &&
              (String(selectedTeamId) === String(currentTeamId) ? (
                <ResetSpaceDataSection
                  key={`reset-${selectedTeam.id}`}
                  team={{ id: selectedTeamId, name: selectedTeam.space.name }}
                />
              ) : (
                <p className="text-sm text-txt-secondary">
                  To reset this space&apos;s data, switch to it first.
                </p>
              ))}
          </div>
        )}
      </div>
    </div>
  )
}
